/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as GuildSoundboardActionCreators from '@app/actions/GuildSoundboardActionCreators';
import * as ModalActionCreators from '@app/actions/ModalActionCreators';
import {modal} from '@app/actions/ModalActionCreators';
import {Input} from '@app/components/form/Input';
import {UploadSlotInfo} from '@app/components/guild/UploadSlotInfo';
import {SoundboardUploadModal} from '@app/components/modals/SoundboardUploadModal';
import {StatusSlate} from '@app/components/modals/shared/StatusSlate';
import {Spinner} from '@app/components/uikit/Spinner';
import {Logger} from '@app/lib/Logger';
import {openFilePicker} from '@app/utils/FilePickerUtils';
import {getEmojiURL} from '@app/utils/AvatarUtils';
import {playSoundboardSoundLocally} from '@app/utils/SoundboardPlayer';
import {MAX_GUILD_SOUNDBOARD_SOUNDS} from '@fluxer/constants/src/LimitConstants';
import type {GuildSoundboardSoundWithUser} from '@fluxer/schema/src/domains/guild/GuildSoundboardSchemas';
import {sortBySnowflakeDesc} from '@fluxer/snowflake/src/SnowflakeUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {MagnifyingGlassIcon, PencilSimpleIcon, PlayIcon, TrashIcon, WarningCircleIcon} from '@phosphor-icons/react';
import {matchSorter} from 'match-sorter';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const logger = new Logger('GuildSoundboardTab');

const UNCATEGORIZED = ' uncategorized';

function SoundboardSoundRow({
	sound,
	onPlay,
	onEdit,
	onDelete,
}: {
	sound: GuildSoundboardSoundWithUser;
	onPlay: () => void;
	onEdit: () => void;
	onDelete: () => void;
}) {
	const {t} = useLingui();
	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: '0.75rem',
				padding: '0.5rem 0.75rem',
				borderRadius: '0.5rem',
				background: 'var(--background-secondary)',
			}}
		>
			<button
				type="button"
				onClick={onPlay}
				aria-label={t`Play sound`}
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: '2.25rem',
					height: '2.25rem',
					borderRadius: '50%',
					border: 'none',
					cursor: 'pointer',
					background: 'var(--background-tertiary)',
					fontSize: '1.25rem',
				}}
			>
				{sound.emoji_id ? (
					<img src={getEmojiURL({id: sound.emoji_id})} alt="" width={22} height={22} />
				) : sound.emoji_name ? (
					<span>{sound.emoji_name}</span>
				) : (
					<PlayIcon weight="fill" size={18} />
				)}
			</button>
			<div style={{display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0}}>
				<span style={{fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis'}}>
					{sound.name}
				</span>
				<span style={{fontSize: '0.75rem', color: 'var(--text-primary-muted)'}}>
					{Math.round(sound.volume * 100)}% · {sound.user?.username ?? ''}
				</span>
			</div>
			<button
				type="button"
				onClick={onEdit}
				aria-label={t`Edit sound`}
				style={{border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-primary-muted)'}}
			>
				<PencilSimpleIcon size={18} />
			</button>
			<button
				type="button"
				onClick={onDelete}
				aria-label={t`Delete sound`}
				style={{border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--status-danger, #d83c3e)'}}
			>
				<TrashIcon size={18} />
			</button>
		</div>
	);
}

const GuildSoundboardTab: React.FC<{guildId: string}> = observer(function GuildSoundboardTab({guildId}) {
	const {t} = useLingui();
	const [sounds, setSounds] = useState<ReadonlyArray<GuildSoundboardSoundWithUser>>([]);
	const [fetchStatus, setFetchStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle');
	const [searchQuery, setSearchQuery] = useState('');

	const fetchSounds = useCallback(async () => {
		try {
			setFetchStatus('pending');
			const list = await GuildSoundboardActionCreators.list(guildId);
			setSounds(Object.freeze(sortBySnowflakeDesc([...list])));
			setFetchStatus('success');
		} catch (error) {
			logger.error('Failed to fetch soundboard sounds', error);
			setFetchStatus('error');
		}
	}, [guildId]);

	useEffect(() => {
		if (fetchStatus === 'idle') {
			void fetchSounds();
		}
	}, [fetchStatus, fetchSounds]);

	const existingCategories = useMemo(() => {
		const set = new Set<string>();
		for (const sound of sounds) {
			if (sound.category) {
				set.add(sound.category);
			}
		}
		return [...set].sort((a, b) => a.localeCompare(b));
	}, [sounds]);

	const handleUpload = useCallback(async () => {
		const [file] = await openFilePicker({accept: 'audio/*'});
		if (file) {
			ModalActionCreators.push(
				modal(() => (
					<SoundboardUploadModal
						guildId={guildId}
						file={file}
						existingCategories={existingCategories}
						onSuccess={fetchSounds}
					/>
				)),
			);
		}
	}, [guildId, existingCategories, fetchSounds]);

	const handleEdit = useCallback(
		(sound: GuildSoundboardSoundWithUser) => {
			ModalActionCreators.push(
				modal(() => (
					<SoundboardUploadModal
						guildId={guildId}
						existingSound={sound}
						existingCategories={existingCategories}
						onSuccess={fetchSounds}
					/>
				)),
			);
		},
		[guildId, existingCategories, fetchSounds],
	);

	const handleDelete = useCallback(
		async (sound: GuildSoundboardSoundWithUser) => {
			setSounds((prev) => prev.filter((s) => s.id !== sound.id));
			try {
				await GuildSoundboardActionCreators.remove(guildId, sound.id);
			} catch (error) {
				logger.error('Failed to delete soundboard sound', error);
				void fetchSounds();
			}
		},
		[guildId, fetchSounds],
	);

	const filteredSounds = useMemo(() => {
		if (!searchQuery) return sounds;
		return matchSorter(sounds, searchQuery, {keys: [(s) => s.name, (s) => s.category ?? '']});
	}, [sounds, searchQuery]);

	const grouped = useMemo(() => {
		const map = new Map<string, Array<GuildSoundboardSoundWithUser>>();
		for (const sound of filteredSounds) {
			const key = sound.category ?? UNCATEGORIZED;
			const arr = map.get(key) ?? [];
			arr.push(sound);
			map.set(key, arr);
		}
		return [...map.entries()].sort(([a], [b]) => {
			if (a === UNCATEGORIZED) return 1;
			if (b === UNCATEGORIZED) return -1;
			return a.localeCompare(b);
		});
	}, [filteredSounds]);

	return (
		<div style={{display: 'flex', flexDirection: 'column', gap: '1rem'}}>
			<Input
				type="text"
				placeholder={t`Search sounds...`}
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				leftIcon={<MagnifyingGlassIcon size={16} weight="bold" />}
			/>

			<UploadSlotInfo
				title={<Trans>Soundboard Sounds</Trans>}
				currentCount={sounds.length}
				maxCount={MAX_GUILD_SOUNDBOARD_SOUNDS}
				uploadButtonText={<Trans>Upload Sound</Trans>}
				onUploadClick={handleUpload}
				description={
					<Trans>
						Soundboard sounds are short audio clips (up to 10 seconds) that anyone in a voice channel can play for
						everyone. You can trim the clip, give it a name and emoji, and set its volume.
					</Trans>
				}
			/>

			{fetchStatus === 'pending' && (
				<div style={{display: 'flex', justifyContent: 'center', padding: '2rem 0'}}>
					<Spinner />
				</div>
			)}

			{fetchStatus === 'success' && sounds.length === 0 && (
				<StatusSlate
					Icon={PlayIcon}
					title={t`No Sounds Yet`}
					description={t`Upload a sound to get started.`}
					fullHeight={true}
				/>
			)}

			{fetchStatus === 'success' &&
				grouped.map(([category, categorySounds]) => (
					<div key={category} style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
						<span
							style={{
								fontSize: '0.75rem',
								fontWeight: 700,
								textTransform: 'uppercase',
								color: 'var(--text-primary-muted)',
							}}
						>
							{category === UNCATEGORIZED ? t`Uncategorized` : category}
						</span>
						{categorySounds.map((sound) => (
							<SoundboardSoundRow
								key={sound.id}
								sound={sound}
								onPlay={() => void playSoundboardSoundLocally(sound.id, sound.volume)}
								onEdit={() => handleEdit(sound)}
								onDelete={() => void handleDelete(sound)}
							/>
						))}
					</div>
				))}

			{fetchStatus === 'error' && (
				<StatusSlate
					Icon={WarningCircleIcon}
					title={t`Failed to Load Sounds`}
					description={t`There was an error loading the soundboard. Please try again.`}
					actions={[{text: t`Retry`, onClick: fetchSounds, variant: 'primary'}]}
					fullHeight={true}
				/>
			)}
		</div>
	);
});

export default GuildSoundboardTab;
